/**
 * Clinical anatomy reference for the body diagram.
 * Keyed by muscle id (matches MUSCLE_LABELS in BodyDiagram.jsx).
 *
 * Fields:
 *   name              — display name (mirrors MUSCLE_LABELS)
 *   region            — single canonical region label
 *   actions           — primary actions
 *   painReferral      — typical pain referral patterns
 *   restrictions      — common ROM/postural restrictions when tight
 *   compensations    — patterns therapists see when this muscle is dysfunctional
 *   related           — frequently co-involved muscle ids
 *   treatment         — short technique-agnostic considerations
 *   cautions          — vascular/nerve/structural cautions
 *   origin            — (learning mode)
 *   insertion         — (learning mode)
 *   joints            — joints crossed (learning mode)
 *   clinical          — clinical relevance for massage/bodywork (learning mode)
 */

export const MUSCLE_INFO = {
  // ───────── FACE ─────────
  frontalis: {
    region: "Head / Face",
    actions: ["Elevates eyebrows", "Wrinkles forehead"],
    painReferral: ["Frontal headache", "Above-eye pressure"],
    restrictions: ["Furrowed brow", "Eye fatigue"],
    compensations: ["Chronic forehead tension from screen strain"],
    related: ["temporalis", "occipitalis", "suboccipitals"],
    treatment: ["Light effleurage and friction along forehead", "Pair with occipital release"],
    cautions: ["Avoid heavy pressure near eyes"],
    origin: "Galea aponeurotica",
    insertion: "Skin of eyebrows and forehead",
    joints: ["None (skin attachment)"],
    clinical: "Part of the occipitofrontalis. Releasing forehead tension often softens overall facial guarding.",
  },
  temporalis: {
    region: "Head / Face",
    actions: ["Elevates and retracts mandible (closes jaw)"],
    painReferral: ["Temporal headache", "Tooth pain (upper)", "TMJ region"],
    restrictions: ["Reduced jaw opening", "TMJ clicking"],
    compensations: ["Clenching, bruxism, forward head posture"],
    related: ["masseter", "scm", "suboccipitals"],
    treatment: ["Gentle circular pressure on temples", "Combine with masseter and intraoral work where licensed"],
    cautions: ["Avoid superficial temporal artery (palpable pulse)"],
    origin: "Temporal fossa",
    insertion: "Coronoid process of mandible",
    joints: ["Temporomandibular (TMJ)"],
    clinical: "Often hypertonic in patients with jaw clenching, stress, or chronic headaches.",
  },
  orbicularis_oculi: {
    region: "Head / Face",
    actions: ["Closes eyelids", "Squinting"],
    painReferral: ["Eye fatigue", "Periorbital tension"],
    restrictions: ["Tired squinting expression"],
    compensations: ["Screen strain, photophobia guarding"],
    related: ["frontalis", "temporalis"],
    treatment: ["Very light circular pressure around orbital rim"],
    cautions: ["Avoid direct eye pressure"],
    origin: "Medial palpebral ligament, frontal bone",
    insertion: "Lateral palpebral raphe",
    joints: ["None"],
    clinical: "Light work here often reduces tension headache symptoms.",
  },
  masseter: {
    region: "Head / Face",
    actions: ["Elevates mandible (powerful jaw closure)"],
    painReferral: ["Cheek pain", "TMJ pain", "Tooth pain", "Ear pain"],
    restrictions: ["Reduced mouth opening", "TMJ deviation"],
    compensations: ["Clenching, bruxism, chronic stress holding"],
    related: ["temporalis", "scm", "suboccipitals", "scalenes"],
    treatment: ["Cross-fiber friction along ramus of mandible", "Trigger point release at belly"],
    cautions: ["Parotid gland just anterior; avoid heavy pressure on Stensen's duct"],
    origin: "Zygomatic arch",
    insertion: "Angle and ramus of mandible",
    joints: ["TMJ"],
    clinical: "Strongest jaw muscle. Often the primary driver of TMJ dysfunction symptoms.",
  },
  zygomaticus: {
    region: "Head / Face",
    actions: ["Elevates corners of mouth (smiling)"],
    painReferral: ["Cheek tension", "Sinus-like pressure"],
    restrictions: ["Asymmetric smile after dental work"],
    compensations: ["Smile guarding, expressive bracing"],
    related: ["masseter", "orbicularis_oris"],
    treatment: ["Light strokes along zygomatic arch toward mouth"],
    cautions: ["Facial nerve branches; use light pressure"],
    origin: "Zygomatic bone",
    insertion: "Angle of mouth",
    joints: ["None"],
    clinical: "Rarely the primary complaint but contributes to facial fatigue.",
  },
  orbicularis_oris: {
    region: "Head / Face",
    actions: ["Closes and protrudes lips"],
    painReferral: ["Perioral tension"],
    restrictions: ["Reduced lip mobility"],
    compensations: ["Chronic pursing, mouth breathing patterns"],
    related: ["mentalis", "masseter"],
    treatment: ["Very light circular work around mouth"],
    cautions: ["Sensitive area; light pressure only"],
    origin: "Maxilla and mandible",
    insertion: "Skin around lips",
    joints: ["None"],
    clinical: "Often tight in singers, wind musicians, and chronic mouth breathers.",
  },
  mentalis: {
    region: "Head / Face",
    actions: ["Elevates and protrudes lower lip", "Wrinkles chin"],
    painReferral: ["Chin tension"],
    restrictions: ["Chin dimpling at rest"],
    compensations: ["Expression bracing, dental clenching"],
    related: ["masseter", "orbicularis_oris"],
    treatment: ["Gentle circular friction on chin"],
    cautions: ["Mental nerve foramen — light pressure"],
    origin: "Mandible (incisive fossa)",
    insertion: "Skin of chin",
    joints: ["None"],
    clinical: "Tension here often pairs with masseter clenching.",
  },
  occipitalis: {
    region: "Head / Face",
    actions: ["Retracts scalp (with frontalis)"],
    painReferral: ["Occipital headache", "Behind-eye pain"],
    restrictions: ["Scalp tightness at base of skull"],
    compensations: ["Forward head posture, suboccipital hypertonicity"],
    related: ["frontalis", "suboccipitals", "splenius_capitis"],
    treatment: ["Suboccipital release with finger pads", "Sustained pressure at base of skull"],
    cautions: ["Greater occipital nerve emerges here — avoid sharp pressure"],
    origin: "Occipital and temporal bones",
    insertion: "Galea aponeurotica",
    joints: ["None"],
    clinical: "Common trigger zone for tension-type headaches and cervicogenic headaches.",
  },

  // ───────── ANTERIOR NECK ─────────
  scm: {
    region: "Neck",
    actions: ["Unilateral: ipsilateral side-bend + contralateral rotation", "Bilateral: cervical flexion"],
    painReferral: ["Temporal/frontal headache", "Ear pain", "Cheek pain", "Forehead pressure"],
    restrictions: ["Reduced cervical rotation", "Forward head posture"],
    compensations: ["Compensates for weak deep neck flexors"],
    related: ["scalenes", "upper_trapezius", "suboccipitals", "levator_scapulae"],
    treatment: ["Pincer grip from clavicle to mastoid", "Trigger point release in two heads"],
    cautions: ["Carotid artery and jugular vein deep to SCM — never sustained anterior pressure", "Spinal accessory nerve passes through"],
    origin: "Sternal head: manubrium; clavicular head: medial clavicle",
    insertion: "Mastoid process and superior nuchal line",
    joints: ["Cervical spine"],
    clinical: "Frequent contributor to whiplash symptoms, cervicogenic headaches, and dizziness.",
  },
  scalenes: {
    region: "Neck",
    actions: ["Cervical lateral flexion", "Elevate first/second ribs (accessory inspiration)"],
    painReferral: ["Lateral neck", "Chest", "Medial border of scapula", "Down the arm to thumb"],
    restrictions: ["Reduced cervical side-bend", "Thoracic outlet symptoms"],
    compensations: ["Chest breathing pattern", "Forward head posture"],
    related: ["scm", "pec_minor", "upper_trapezius"],
    treatment: ["Gentle stripping anterior to upper traps", "Release while patient exhales"],
    cautions: ["Brachial plexus passes between anterior and middle scalene", "Subclavian artery — light, intelligent pressure only"],
    origin: "Transverse processes C2–C7",
    insertion: "First and second ribs",
    joints: ["Cervical spine, ribs 1-2"],
    clinical: "Major player in thoracic outlet syndrome and chest-dominant breathing.",
  },
  platysma: {
    region: "Neck",
    actions: ["Tenses skin of neck", "Depresses mandible/lower lip"],
    painReferral: ["Anterior neck tension"],
    restrictions: ["Tight, banded look to anterior neck"],
    compensations: ["Chronic facial bracing, post-surgical scarring"],
    related: ["scm", "masseter"],
    treatment: ["Light effleurage along anterior neck"],
    cautions: ["Very superficial — light pressure only", "Carotid pulse deep"],
    origin: "Fascia over pec major/deltoid",
    insertion: "Mandible and skin of lower face",
    joints: ["None directly"],
    clinical: "Worth addressing in post-thyroidectomy or chronic anterior neck guarding.",
  },
  digastric: {
    region: "Neck",
    actions: ["Depresses mandible", "Elevates hyoid during swallowing"],
    painReferral: ["Under jaw, deep ear", "Front of neck"],
    restrictions: ["Swallowing tension", "Mouth opening limitation"],
    compensations: ["TMJ dysfunction, tongue thrust"],
    related: ["masseter", "suprahyoids", "scm"],
    treatment: ["Gentle finger work along inferior mandible border"],
    cautions: ["Submandibular gland, facial artery"],
    origin: "Mastoid notch (posterior belly), mandible (anterior belly)",
    insertion: "Hyoid bone",
    joints: ["TMJ, hyoid complex"],
    clinical: "Important for TMJ work and swallowing dysfunction.",
  },
  suprahyoids: {
    region: "Neck",
    actions: ["Elevate hyoid during swallowing", "Depress mandible"],
    painReferral: ["Throat tightness", "Submental discomfort"],
    restrictions: ["Swallowing difficulty"],
    compensations: ["Forward head posture, tongue protrusion"],
    related: ["digastric", "infrahyoids", "masseter"],
    treatment: ["Light pincer work along hyoid"],
    cautions: ["Avoid direct pressure on the hyoid bone"],
    origin: "Mandible, styloid, mastoid",
    insertion: "Hyoid bone",
    joints: ["Hyoid complex"],
    clinical: "Useful for chronic globus sensation and dental holding patterns.",
  },
  infrahyoids: {
    region: "Neck",
    actions: ["Depress hyoid and larynx during swallowing/speech"],
    painReferral: ["Anterior throat", "Larynx pressure"],
    restrictions: ["Voice fatigue", "Swallowing tension"],
    compensations: ["Forward head, voice strain"],
    related: ["suprahyoids", "scm"],
    treatment: ["Very light strokes lateral to trachea"],
    cautions: ["Thyroid gland, carotid sheath"],
    origin: "Sternum, scapula",
    insertion: "Hyoid bone",
    joints: ["Hyoid complex"],
    clinical: "Often addressed in singers, speakers, and post-intubation patients.",
  },
  longus_colli: {
    region: "Neck",
    actions: ["Deep cervical flexion", "Stabilizes cervical spine"],
    painReferral: ["Deep anterior neck"],
    restrictions: ["Poor head-on-neck control", "Forward head posture"],
    compensations: ["SCM and scalene overuse"],
    related: ["scm", "scalenes", "suboccipitals"],
    treatment: ["Indirect work; activate via chin-tuck reeducation"],
    cautions: ["Deep to carotid sheath — generally not directly palpated"],
    origin: "Anterior vertebral bodies C1-T3",
    insertion: "Anterior vertebral bodies C1-C6",
    joints: ["Cervical spine"],
    clinical: "Often weak/inhibited in chronic whiplash and forward head posture — pair with cueing.",
  },

  // ───────── POSTERIOR NECK / DEEP CERVICAL ─────────
  suboccipitals: {
    region: "Neck",
    actions: ["Fine head-on-neck movements", "Cervical extension", "Rotation"],
    painReferral: ["Wraps over head to eye/forehead", "Behind-eye headache"],
    restrictions: ["Reduced chin tuck", "Stiff upper cervical"],
    compensations: ["Forward head posture, visual strain"],
    related: ["upper_trapezius", "splenius_capitis", "scm", "occipitalis"],
    treatment: ["Sustained finger-pad pressure under occipital ridge", "Indirect supine traction"],
    cautions: ["Vertebral artery, greater occipital nerve — slow, sustained pressure only"],
    origin: "C1 and C2 (axis/atlas)",
    insertion: "Occiput",
    joints: ["Atlanto-occipital, atlanto-axial"],
    clinical: "Primary driver of tension headaches and dizziness in postural patients.",
  },
  splenius_capitis: {
    region: "Neck",
    actions: ["Cervical extension", "Ipsilateral rotation and side-bend"],
    painReferral: ["Top of head", "Eye region"],
    restrictions: ["Reduced rotation toward opposite side"],
    compensations: ["Levator scapulae overuse"],
    related: ["splenius_cervicis", "levator_scapulae", "upper_trapezius"],
    treatment: ["Stripping along lateral cervical spine to mastoid"],
    cautions: ["Vertebral artery deeper; avoid aggressive cervical extension"],
    origin: "Lower ligamentum nuchae, T1-T3 spinous processes",
    insertion: "Mastoid process and superior nuchal line",
    joints: ["Cervical spine"],
    clinical: "Frequently involved in cervicogenic headaches.",
  },
  splenius_cervicis: {
    region: "Neck",
    actions: ["Cervical extension", "Ipsilateral rotation and side-bend"],
    painReferral: ["Back of neck", "Behind eye"],
    restrictions: ["Stiff lower cervical rotation"],
    compensations: ["Upper trapezius dominance"],
    related: ["splenius_capitis", "levator_scapulae"],
    treatment: ["Deep stripping lateral to spinous processes"],
    cautions: ["Greater occipital nerve at superior edge"],
    origin: "T3-T6 spinous processes",
    insertion: "Transverse processes C1-C3",
    joints: ["Cervical spine"],
    clinical: "Often involved when patient reports 'crick in the neck'.",
  },
  semispinalis_capitis: {
    region: "Neck",
    actions: ["Cervical extension", "Contralateral rotation"],
    painReferral: ["Band-like headache wrapping head"],
    restrictions: ["Chronic posterior neck tightness"],
    compensations: ["Upper crossed syndrome"],
    related: ["suboccipitals", "splenius_capitis"],
    treatment: ["Cross-fiber along nuchal line", "Sustained release"],
    cautions: ["Greater occipital nerve traverses this muscle"],
    origin: "Transverse processes C7-T6",
    insertion: "Between superior and inferior nuchal lines",
    joints: ["Cervical and upper thoracic spine"],
    clinical: "Major contributor to occipital headaches.",
  },
  semispinalis_cervicis: {
    region: "Neck",
    actions: ["Cervical extension", "Contralateral rotation"],
    painReferral: ["Cervicothoracic junction"],
    restrictions: ["Stiff thoracic-to-cervical transition"],
    compensations: ["Hinge points at C7-T1"],
    related: ["semispinalis_capitis", "multifidus_cervicis"],
    treatment: ["Deep stripping between spinous and transverse processes"],
    cautions: ["Stay lateral to spinous processes"],
    origin: "Transverse processes T1-T6",
    insertion: "Spinous processes C2-C5",
    joints: ["Cervical and upper thoracic"],
    clinical: "Stabilizer of cervical spine; often weak in poor posture.",
  },
  multifidus_cervicis: {
    region: "Neck",
    actions: ["Segmental cervical stabilization", "Extension"],
    painReferral: ["Deep, focal cervical pain"],
    restrictions: ["Segmental hypomobility"],
    compensations: ["Global extensor overuse when multifidi are weak"],
    related: ["semispinalis_cervicis", "suboccipitals"],
    treatment: ["Specific segmental pressure; pair with motor control cueing"],
    cautions: ["Stay lateral to spinous processes"],
    origin: "Articular processes C4-T3",
    insertion: "Spinous processes 2-4 segments above",
    joints: ["Cervical spine segmental"],
    clinical: "Critical proprioceptors; their dysfunction underlies many chronic neck conditions.",
  },

  // ───────── SHOULDERS (ANTERIOR/LATERAL/POSTERIOR DELTOID) ─────────
  anterior_deltoid: {
    region: "Shoulders",
    actions: ["Shoulder flexion", "Internal rotation", "Horizontal adduction"],
    painReferral: ["Anterior shoulder", "Deltoid tubercle"],
    restrictions: ["Reduced shoulder extension"],
    compensations: ["Pec major dominance, rounded shoulders"],
    related: ["pec_major_clavicular", "biceps_brachii", "supraspinatus"],
    treatment: ["Cross-fiber friction at deltoid tuberosity", "Pin and stretch"],
    cautions: ["Cephalic vein at deltopectoral groove"],
    origin: "Lateral third of clavicle",
    insertion: "Deltoid tuberosity of humerus",
    joints: ["Glenohumeral"],
    clinical: "Frequently overworked in pressing patterns and forward shoulder posture.",
  },
  lateral_deltoid: {
    region: "Shoulders",
    actions: ["Shoulder abduction (15°-90°)"],
    painReferral: ["Lateral arm to elbow"],
    restrictions: ["Reduced abduction"],
    compensations: ["Upper trapezius hiking"],
    related: ["supraspinatus", "upper_trapezius"],
    treatment: ["Stripping from acromion to deltoid tuberosity"],
    cautions: ["Axillary nerve wraps surgical neck of humerus deep"],
    origin: "Acromion",
    insertion: "Deltoid tuberosity",
    joints: ["Glenohumeral"],
    clinical: "Often tender in repetitive overhead workers and impingement cases.",
  },
  posterior_deltoid: {
    region: "Shoulders",
    actions: ["Shoulder extension", "External rotation", "Horizontal abduction"],
    painReferral: ["Posterior shoulder", "Mid-deltoid"],
    restrictions: ["Reduced horizontal adduction"],
    compensations: ["Underactive in rounded-shoulder posture"],
    related: ["infraspinatus", "teres_minor", "middle_trapezius"],
    treatment: ["Cross-fiber and stripping with arm supported"],
    cautions: ["Axillary nerve deep"],
    origin: "Spine of scapula",
    insertion: "Deltoid tuberosity",
    joints: ["Glenohumeral"],
    clinical: "Often weak and lengthened; pair with posterior chain activation cues.",
  },

  // ───────── CHEST / SERRATUS ─────────
  pec_major_clavicular: {
    region: "Chest/Pecs",
    actions: ["Shoulder flexion", "Horizontal adduction", "Internal rotation"],
    painReferral: ["Anterior shoulder", "Medial arm", "Sternum"],
    restrictions: ["Rounded shoulders", "Reduced shoulder extension"],
    compensations: ["Forward head posture, kyphosis"],
    related: ["pec_minor", "anterior_deltoid", "biceps_brachii"],
    treatment: ["Stripping from clavicle laterally", "Stretch with arm externally rotated"],
    cautions: ["Avoid breast tissue and axillary contents"],
    origin: "Medial half of clavicle",
    insertion: "Lateral lip of bicipital groove",
    joints: ["Glenohumeral"],
    clinical: "Cornerstone of upper crossed syndrome — release before posterior chain activation.",
  },
  pec_major_sternal: {
    region: "Chest/Pecs",
    actions: ["Shoulder adduction", "Horizontal adduction", "Internal rotation"],
    painReferral: ["Sternum, breast, medial arm"],
    restrictions: ["Limited shoulder abduction"],
    compensations: ["Upper crossed syndrome"],
    related: ["pec_major_clavicular", "pec_minor", "lats_upper"],
    treatment: ["Cross-fiber along sternum", "Pin and stretch toward 90° abduction"],
    cautions: ["Breast tissue — communicate clearly and drape professionally"],
    origin: "Sternum, costal cartilages 2-6",
    insertion: "Lateral lip of bicipital groove",
    joints: ["Glenohumeral"],
    clinical: "Common in patients with chronic computer use and bench-press training.",
  },
  pec_minor: {
    region: "Chest/Pecs",
    actions: ["Protracts and depresses scapula", "Elevates ribs"],
    painReferral: ["Anterior shoulder", "Down medial arm to ulnar fingers (TOS pattern)"],
    restrictions: ["Anterior tilt of scapula", "Reduced shoulder flexion"],
    compensations: ["Thoracic outlet syndrome symptoms"],
    related: ["scalenes", "pec_major_clavicular", "subscapularis"],
    treatment: ["Pincer grip under axilla with arm supported", "Sustained release"],
    cautions: ["Brachial plexus and axillary artery pass beneath — slow, ethical pressure"],
    origin: "Ribs 3-5",
    insertion: "Coracoid process of scapula",
    joints: ["Scapulothoracic"],
    clinical: "Often the hidden culprit in thoracic outlet and 'shoulder' pain.",
  },
  serratus_anterior: {
    region: "Chest/Pecs",
    actions: ["Protracts and upwardly rotates scapula", "Stabilizes scapula on rib cage"],
    painReferral: ["Lateral chest", "Inferior scapula", "Down medial arm"],
    restrictions: ["Scapular winging", "Reduced overhead reach"],
    compensations: ["Upper trap dominance in overhead motion"],
    related: ["lower_trapezius", "rhomboids"],
    treatment: ["Side-lying access; stripping along lateral ribs"],
    cautions: ["Long thoracic nerve runs on its surface"],
    origin: "Lateral surface of ribs 1-9",
    insertion: "Medial border of scapula (anterior surface)",
    joints: ["Scapulothoracic"],
    clinical: "Critical for healthy scapulohumeral rhythm — often weak and inhibited.",
  },

  // ───────── ANTERIOR ARM / FOREARM ─────────
  biceps_brachii: {
    region: "Hands/Forearms",
    actions: ["Elbow flexion", "Forearm supination", "Assists shoulder flexion"],
    painReferral: ["Anterior shoulder", "Antecubital fossa"],
    restrictions: ["Reduced elbow extension with supination"],
    compensations: ["Brachialis overuse, shoulder rounding"],
    related: ["brachialis", "anterior_deltoid", "pec_major_clavicular"],
    treatment: ["Stripping from origin to insertion", "Pin and stretch in supination"],
    cautions: ["Brachial artery and median nerve at antecubital fossa"],
    origin: "Long head: supraglenoid tubercle; Short head: coracoid process",
    insertion: "Radial tuberosity and bicipital aponeurosis",
    joints: ["Glenohumeral, elbow, radioulnar"],
    clinical: "Long head tendon is a frequent pain generator in shoulder cases.",
  },
  brachialis: {
    region: "Hands/Forearms",
    actions: ["Elbow flexion (primary, regardless of forearm position)"],
    painReferral: ["Base of thumb", "Anterior elbow"],
    restrictions: ["Reduced elbow extension"],
    compensations: ["Biceps dominance"],
    related: ["biceps_brachii", "brachioradialis"],
    treatment: ["Deep stripping along lateral biceps belly"],
    cautions: ["Musculocutaneous nerve, radial nerve laterally"],
    origin: "Distal anterior humerus",
    insertion: "Coronoid process and tuberosity of ulna",
    joints: ["Elbow"],
    clinical: "True workhorse of elbow flexion — often missed in arm work.",
  },
  brachioradialis: {
    region: "Hands/Forearms",
    actions: ["Elbow flexion in neutral grip", "Assists pronation/supination toward neutral"],
    painReferral: ["Lateral elbow (mimics tennis elbow)", "Dorsum of hand"],
    restrictions: ["Elbow extension with pronation/supination"],
    compensations: ["Grip overuse, repetitive strain"],
    related: ["wrist_extensors", "biceps_brachii"],
    treatment: ["Stripping along radial side of forearm"],
    cautions: ["Radial nerve and superficial branches"],
    origin: "Lateral supracondylar ridge of humerus",
    insertion: "Distal radius (styloid)",
    joints: ["Elbow"],
    clinical: "Frequently involved in 'tennis elbow' and repetitive grip cases.",
  },
  wrist_flexors: {
    region: "Hands/Forearms",
    actions: ["Wrist flexion", "Finger flexion (deeper layers)"],
    painReferral: ["Medial elbow (golfer's elbow)", "Palmar wrist"],
    restrictions: ["Reduced wrist extension"],
    compensations: ["Grip dominance, mouse/keyboard overuse"],
    related: ["wrist_extensors", "brachioradialis"],
    treatment: ["Stripping from medial epicondyle distally", "Cross-fiber at common flexor origin"],
    cautions: ["Median and ulnar nerves at wrist"],
    origin: "Medial epicondyle of humerus and forearm bones",
    insertion: "Carpals, metacarpals, phalanges",
    joints: ["Elbow, wrist, fingers"],
    clinical: "Central to medial epicondylitis and carpal tunnel-associated complaints.",
  },

  // ───────── CORE ─────────
  rectus_abdominis_upper: {
    region: "Abdomen/Core",
    actions: ["Trunk flexion", "Compresses abdominal contents"],
    painReferral: ["Mid-back", "Xiphoid area"],
    restrictions: ["Reduced thoracic extension"],
    compensations: ["Chest breathing, kyphosis"],
    related: ["psoas_iliacus", "external_obliques", "rectus_abdominis_lower"],
    treatment: ["Light cross-fiber along linea alba boundaries", "Diaphragmatic breathing cues"],
    cautions: ["Avoid pressure on xiphoid; abdominal contents"],
    origin: "Pubic crest and symphysis",
    insertion: "Costal cartilages 5-7, xiphoid process",
    joints: ["Thoracolumbar"],
    clinical: "Often shortened in chronic flexion postures (desk workers, drivers).",
  },
  rectus_abdominis_lower: {
    region: "Abdomen/Core",
    actions: ["Trunk flexion", "Posterior pelvic tilt"],
    painReferral: ["Lower abdomen", "Suprapubic"],
    restrictions: ["Reduced trunk extension"],
    compensations: ["Hip flexor dominance, anterior pelvic tilt"],
    related: ["psoas_iliacus", "external_obliques"],
    treatment: ["Very light superficial work; abdominal binding awareness"],
    cautions: ["Bladder, abdominal aorta deep"],
    origin: "Pubic crest and symphysis",
    insertion: "Costal cartilages 5-7, xiphoid process",
    joints: ["Lumbar, pelvis"],
    clinical: "Postpartum and post-surgical patients require special care.",
  },
  external_obliques: {
    region: "Abdomen/Core",
    actions: ["Contralateral trunk rotation", "Ipsilateral side-bend", "Trunk flexion"],
    painReferral: ["Lateral abdomen", "Lower ribs", "Pelvis"],
    restrictions: ["Reduced rotation, side-bend"],
    compensations: ["Quadratus lumborum overuse"],
    related: ["quadratus_lumborum", "rectus_abdominis_upper"],
    treatment: ["Side-lying stripping along lateral ribs"],
    cautions: ["Lower ribs are flexible — moderate pressure only"],
    origin: "Lower 8 ribs",
    insertion: "Iliac crest, linea alba",
    joints: ["Thoracolumbar, pelvis"],
    clinical: "Often weak in low back pain patients — pair with rotation drills.",
  },

  // ───────── HIPS / THIGHS ─────────
  psoas_iliacus: {
    region: "Hip Flexors",
    actions: ["Hip flexion", "External rotation", "Lumbar stabilization"],
    painReferral: ["Lumbar (deep)", "Anterior hip", "Anterior thigh"],
    restrictions: ["Reduced hip extension (Thomas test)", "Anterior pelvic tilt"],
    compensations: ["Lumbar hyperlordosis, low back pain"],
    related: ["quadratus_lumborum", "rectus_femoris", "tfl"],
    treatment: ["Supine slow access lateral to rectus abdominis", "Communicate constantly"],
    cautions: ["Abdominal aorta, ureter, intestines — slow, ethical, well-communicated work only"],
    origin: "Psoas: T12-L5 vertebral bodies; Iliacus: iliac fossa",
    insertion: "Lesser trochanter of femur",
    joints: ["Hip, lumbar spine"],
    clinical: "Central in chronic low back pain. Always check before chasing pain elsewhere.",
  },
  tfl: {
    region: "Hip Flexors",
    actions: ["Hip flexion, abduction, internal rotation", "Tensions IT band"],
    painReferral: ["Lateral hip", "Lateral thigh", "Lateral knee"],
    restrictions: ["Reduced hip adduction and external rotation"],
    compensations: ["Glute medius weakness, Trendelenburg gait"],
    related: ["IT_band", "gluteus_medius", "vastus_lateralis"],
    treatment: ["Side-lying focused pressure", "Pair with glute medius activation"],
    cautions: ["Lateral femoral cutaneous nerve nearby"],
    origin: "Anterior iliac crest, ASIS",
    insertion: "Iliotibial band → lateral tibia (Gerdy's tubercle)",
    joints: ["Hip, knee"],
    clinical: "Common driver of IT band syndrome and lateral knee pain.",
  },
  sartorius: {
    region: "Hip Flexors",
    actions: ["Hip flexion, abduction, external rotation", "Knee flexion"],
    painReferral: ["Anterior thigh, medial knee"],
    restrictions: ["Limited 'cross-leg' position"],
    compensations: ["Hip flexor dominance"],
    related: ["rectus_femoris", "tfl", "adductor_longus"],
    treatment: ["Stripping along diagonal anterior thigh"],
    cautions: ["Femoral artery/nerve at proximal medial thigh"],
    origin: "ASIS",
    insertion: "Pes anserinus (proximal medial tibia)",
    joints: ["Hip, knee"],
    clinical: "Pes anserine tenderness often involves sartorius along with gracilis and semitendinosus.",
  },
  rectus_femoris: {
    region: "Quads",
    actions: ["Hip flexion", "Knee extension"],
    painReferral: ["Anterior thigh", "Anterior knee"],
    restrictions: ["Reduced knee flexion with hip extension"],
    compensations: ["Anterior pelvic tilt, lumbar lordosis"],
    related: ["psoas_iliacus", "tfl", "vastus_lateralis"],
    treatment: ["Stripping from AIIS to patellar tendon", "Pin and stretch (Thomas position)"],
    cautions: ["Femoral nerve at proximal thigh"],
    origin: "AIIS and supra-acetabular groove",
    insertion: "Tibial tuberosity via patellar tendon",
    joints: ["Hip, knee"],
    clinical: "Only quad that crosses both hip and knee — central in anterior pelvic tilt cases.",
  },
  vastus_lateralis: {
    region: "Quads",
    actions: ["Knee extension"],
    painReferral: ["Lateral thigh", "Lateral knee"],
    restrictions: ["Patellar tracking issues (lateral pull)"],
    compensations: ["IT band tension, runner's knee"],
    related: ["IT_band", "vastus_medialis", "rectus_femoris"],
    treatment: ["Side-lying or supine stripping", "Cross-fiber along distal lateral quad"],
    cautions: ["IT band fascia overlying — pressure should be tolerable"],
    origin: "Greater trochanter, linea aspera",
    insertion: "Patella → tibial tuberosity",
    joints: ["Knee"],
    clinical: "Frequent contributor to patellofemoral pain when overactive vs. VMO.",
  },
  vastus_medialis: {
    region: "Quads",
    actions: ["Knee extension", "VMO stabilizes patella medially"],
    painReferral: ["Medial knee", "Anteromedial thigh"],
    restrictions: ["Knee instability in terminal extension"],
    compensations: ["Patellar maltracking"],
    related: ["vastus_lateralis", "adductor_longus"],
    treatment: ["Stripping along medial distal thigh", "Pair with terminal knee extension activation"],
    cautions: ["Saphenous nerve and great saphenous vein medially"],
    origin: "Medial linea aspera",
    insertion: "Patella → tibial tuberosity",
    joints: ["Knee"],
    clinical: "Often weak in knee pain patients; activation as important as release.",
  },
  adductor_longus: {
    region: "Hip Flexors",
    actions: ["Hip adduction, flexion, slight internal rotation"],
    painReferral: ["Groin", "Medial thigh", "Anterior knee"],
    restrictions: ["Reduced hip abduction"],
    compensations: ["Hip flexor dominance, groin strain risk"],
    related: ["adductor_magnus", "gracilis", "psoas_iliacus"],
    treatment: ["Supine stripping medial to sartorius", "Side-lying access"],
    cautions: ["Femoral artery/vein in femoral triangle proximally"],
    origin: "Pubic body",
    insertion: "Middle third of linea aspera",
    joints: ["Hip"],
    clinical: "Frequent in soccer/hockey athletes; check in chronic groin pain.",
  },
  adductor_magnus: {
    region: "Hip Flexors",
    actions: ["Hip adduction, extension (posterior fibers)"],
    painReferral: ["Medial thigh", "Groin", "Pelvic floor referral possible"],
    restrictions: ["Reduced hip abduction and external rotation"],
    compensations: ["Glute weakness, pelvic floor tension"],
    related: ["adductor_longus", "gluteus_maximus", "hamstrings_group"],
    treatment: ["Deep stripping in side-lying", "Pair with glute work"],
    cautions: ["Adductor hiatus — femoral vessels become popliteal"],
    origin: "Inferior pubic ramus, ischial tuberosity",
    insertion: "Linea aspera and adductor tubercle",
    joints: ["Hip"],
    clinical: "The largest adductor; often involved in pelvic floor and low back patterns.",
  },
  gracilis: {
    region: "Hip Flexors",
    actions: ["Hip adduction", "Knee flexion and internal rotation"],
    painReferral: ["Medial thigh", "Medial knee (pes anserinus)"],
    restrictions: ["Reduced hip abduction with knee extension"],
    compensations: ["Knee valgus, pes anserine bursitis"],
    related: ["adductor_longus", "sartorius", "semitendinosus"],
    treatment: ["Long stripping along medial thigh"],
    cautions: ["Saphenous nerve medially"],
    origin: "Inferior pubic ramus",
    insertion: "Pes anserinus (proximal medial tibia)",
    joints: ["Hip, knee"],
    clinical: "Crosses two joints — common in medial knee/groin presentations.",
  },

  // ───────── LOWER LEG ─────────
  tibialis_anterior: {
    region: "Calves",
    actions: ["Dorsiflexion", "Inversion"],
    painReferral: ["Shin (anterior compartment)", "Dorsum of foot"],
    restrictions: ["Reduced plantarflexion"],
    compensations: ["Shin splints, foot slap in gait"],
    related: ["peroneals", "gastrocnemius_medial"],
    treatment: ["Stripping along anterolateral tibia", "Cross-fiber at musculotendinous junction"],
    cautions: ["Anterior tibial artery and deep peroneal nerve deep"],
    origin: "Lateral tibia and interosseous membrane",
    insertion: "Medial cuneiform, base of 1st metatarsal",
    joints: ["Ankle, subtalar"],
    clinical: "Cardinal in shin splints and gait dysfunction.",
  },
  peroneals: {
    region: "Calves",
    actions: ["Eversion", "Plantarflexion assist"],
    painReferral: ["Lateral leg", "Lateral ankle", "Lateral foot"],
    restrictions: ["Reduced inversion"],
    compensations: ["Lateral ankle instability"],
    related: ["tibialis_anterior", "gastrocnemius_lateral"],
    treatment: ["Stripping along fibula", "Address at lateral malleolus"],
    cautions: ["Common peroneal nerve at fibular head — avoid pressure"],
    origin: "Fibula",
    insertion: "Base of 1st and 5th metatarsals",
    joints: ["Ankle, foot"],
    clinical: "Frequently involved in chronic ankle sprains and balance issues.",
  },

  // ───────── UPPER BACK / SCAPULA ─────────
  upper_trapezius: {
    region: "Upper Back",
    actions: ["Scapular elevation", "Upward rotation", "Cervical extension/side-bend"],
    painReferral: ["Side of neck", "Temporal headache", "Behind eye"],
    restrictions: ["Reduced cervical side-bend opposite", "Shoulder hiking"],
    compensations: ["Forward head, rounded shoulders, breathing dysfunction"],
    related: ["levator_scapulae", "scm", "scalenes", "suboccipitals"],
    treatment: ["Sustained pressure on belly", "Cross-fiber at superior border", "Patient education on shoulder-down breathing"],
    cautions: ["Spinal accessory nerve runs along its deep border"],
    origin: "External occipital protuberance, ligamentum nuchae, C7",
    insertion: "Lateral clavicle, acromion",
    joints: ["Scapulothoracic, cervical"],
    clinical: "The most overworked muscle in modern desk-based life. Almost always involved in neck/headache cases.",
  },
  middle_trapezius: {
    region: "Upper Back",
    actions: ["Scapular retraction"],
    painReferral: ["Interscapular region"],
    restrictions: ["Rounded shoulders"],
    compensations: ["Pec dominance, scapular protraction"],
    related: ["rhomboids", "lower_trapezius"],
    treatment: ["Cross-fiber between scapula and spine", "Activation cues for retraction"],
    cautions: ["Stay lateral to spinous processes"],
    origin: "Spinous processes T1-T5",
    insertion: "Spine of scapula",
    joints: ["Scapulothoracic"],
    clinical: "Usually weak and lengthened in upper crossed syndrome — release antagonists first.",
  },
  lower_trapezius: {
    region: "Mid Back",
    actions: ["Scapular depression and upward rotation"],
    painReferral: ["Mid-thoracic", "Inferior scapula"],
    restrictions: ["Difficulty maintaining scapular depression"],
    compensations: ["Upper trap dominance in overhead motion"],
    related: ["serratus_anterior", "middle_trapezius"],
    treatment: ["Long strokes along thoracic spine to scapular spine", "Activation: prone Y-raises"],
    cautions: ["Stay lateral to spinous processes"],
    origin: "Spinous processes T6-T12",
    insertion: "Medial end of scapular spine",
    joints: ["Scapulothoracic"],
    clinical: "Key to fixing upper trap dominance — strengthen and activate, don't just release.",
  },
  rhomboids: {
    region: "Upper Back",
    actions: ["Scapular retraction", "Downward rotation", "Elevation"],
    painReferral: ["Interscapular, medial border of scapula"],
    restrictions: ["Rounded shoulders", "Scapular protraction"],
    compensations: ["Pec/serratus dominance"],
    related: ["middle_trapezius", "levator_scapulae"],
    treatment: ["Cross-fiber along medial border of scapula", "Prone access ideal"],
    cautions: ["Dorsal scapular nerve runs medial to scapula"],
    origin: "Spinous processes C7-T5",
    insertion: "Medial border of scapula",
    joints: ["Scapulothoracic"],
    clinical: "Classic 'between the shoulder blades' pain — often weak and overstretched, not 'tight.'",
  },
  levator_scapulae: {
    region: "Neck",
    actions: ["Scapular elevation", "Cervical side-bend and rotation"],
    painReferral: ["Angle of neck", "Top of scapula", "Suboccipital region"],
    restrictions: ["Reduced cervical contralateral rotation", "Stiff neck pattern"],
    compensations: ["Upper crossed syndrome, breathing pattern dysfunction"],
    related: ["upper_trapezius", "rhomboids", "splenius_capitis"],
    treatment: ["Stripping from superior angle of scapula superiorly", "Pin and stretch with arm overhead"],
    cautions: ["Dorsal scapular nerve runs adjacent"],
    origin: "Transverse processes C1-C4",
    insertion: "Superior angle of scapula",
    joints: ["Scapulothoracic, cervical"],
    clinical: "Frequent 'crick in the neck' culprit. Twists 90° during its course, often a focal trigger point at the scapular insertion.",
  },

  // ───────── ROTATOR CUFF ─────────
  supraspinatus: {
    region: "Shoulders",
    actions: ["Initiates shoulder abduction (0-15°)", "Stabilizes humeral head"],
    painReferral: ["Lateral shoulder", "Down lateral arm to deltoid tubercle"],
    restrictions: ["Painful arc 60-120° abduction"],
    compensations: ["Upper trap hiking, impingement"],
    related: ["upper_trapezius", "lateral_deltoid", "infraspinatus"],
    treatment: ["Stripping above scapular spine", "Sustained pressure with arm supported"],
    cautions: ["Subacromial bursa beneath", "Suprascapular nerve"],
    origin: "Supraspinous fossa of scapula",
    insertion: "Greater tubercle of humerus (superior facet)",
    joints: ["Glenohumeral"],
    clinical: "Most commonly torn rotator cuff muscle. Painful arc is hallmark.",
  },
  infraspinatus: {
    region: "Shoulders",
    actions: ["External rotation of humerus", "Stabilizes humeral head posteriorly"],
    painReferral: ["Deep anterior shoulder", "Down anterior arm"],
    restrictions: ["Reduced shoulder internal rotation"],
    compensations: ["Subscapularis dominance, anterior glide of humeral head"],
    related: ["teres_minor", "posterior_deltoid", "supraspinatus"],
    treatment: ["Cross-fiber in infraspinous fossa", "Pin and stretch toward internal rotation"],
    cautions: ["Suprascapular nerve in spinoglenoid notch"],
    origin: "Infraspinous fossa of scapula",
    insertion: "Greater tubercle (middle facet)",
    joints: ["Glenohumeral"],
    clinical: "Trigger points refer to deep anterior shoulder — often misdiagnosed as biceps tendinopathy.",
  },
  teres_minor: {
    region: "Shoulders",
    actions: ["External rotation", "Humeral head stabilization"],
    painReferral: ["Posterior shoulder", "Lateral arm"],
    restrictions: ["Reduced internal rotation"],
    compensations: ["Subscapularis overuse"],
    related: ["infraspinatus", "posterior_deltoid"],
    treatment: ["Stripping along lateral border of scapula"],
    cautions: ["Axillary nerve in quadrangular space"],
    origin: "Lateral border of scapula",
    insertion: "Greater tubercle (inferior facet)",
    joints: ["Glenohumeral"],
    clinical: "Often a hidden contributor to posterior shoulder pain.",
  },
  teres_major: {
    region: "Shoulders",
    actions: ["Adduction", "Extension", "Internal rotation of humerus"],
    painReferral: ["Posterior shoulder", "Down arm"],
    restrictions: ["Reduced shoulder flexion overhead", "Reduced external rotation"],
    compensations: ["Lats dominance, posterior shoulder tightness"],
    related: ["lats_upper", "subscapularis"],
    treatment: ["Stripping along lateral border of scapula inferior to teres minor"],
    cautions: ["Quadrangular space contents"],
    origin: "Inferior angle of scapula",
    insertion: "Medial lip of bicipital groove",
    joints: ["Glenohumeral"],
    clinical: "'Lat's little helper' — frequently restricts overhead reach.",
  },

  // ───────── BACK / SPINE ─────────
  lats_upper: {
    region: "Mid Back",
    actions: ["Shoulder adduction", "Extension", "Internal rotation"],
    painReferral: ["Inferior scapula", "Posterior shoulder", "Down medial arm"],
    restrictions: ["Reduced overhead shoulder flexion"],
    compensations: ["Limited overhead reach", "Lumbar lordosis on shoulder flexion"],
    related: ["teres_major", "lats_lower", "thoracolumbar_fascia"],
    treatment: ["Side-lying stripping at posterior axillary fold", "Pin and stretch in shoulder flexion"],
    cautions: ["Axillary contents at proximal end"],
    origin: "Spinous processes T7-T12, thoracolumbar fascia, iliac crest",
    insertion: "Floor of bicipital groove",
    joints: ["Glenohumeral, scapulothoracic, indirectly lumbar"],
    clinical: "Connects arms to pelvis — restriction here can drive both shoulder and low back issues.",
  },
  lats_lower: {
    region: "Lower Back",
    actions: ["Trunk extension (with lumbar attachment)", "Pelvic stabilization"],
    painReferral: ["Lateral low back", "Posterior axilla"],
    restrictions: ["Reduced lateral flexion"],
    compensations: ["QL dominance, lumbar compression"],
    related: ["quadratus_lumborum", "thoracolumbar_fascia"],
    treatment: ["Side-lying stripping along ribs to iliac crest"],
    cautions: ["Lower ribs are flexible"],
    origin: "Thoracolumbar fascia, iliac crest, lower ribs",
    insertion: "Floor of bicipital groove",
    joints: ["Lumbar, glenohumeral"],
    clinical: "Lateral low back tightness often involves lats more than lumbar paraspinals.",
  },
  erector_spinae: {
    region: "Lower Back",
    actions: ["Trunk extension", "Ipsilateral side-bend", "Spinal stabilization"],
    painReferral: ["Diffuse back pain along the spine", "Sacroiliac region"],
    restrictions: ["Reduced trunk flexion", "Reduced rotation"],
    compensations: ["Lumbar hyperlordosis, hinge points"],
    related: ["quadratus_lumborum", "multifidus_cervicis", "thoracolumbar_fascia"],
    treatment: ["Long stripping along paraspinal groove", "Cross-fiber at thoracolumbar junction"],
    cautions: ["Stay lateral to spinous processes", "Kidneys at T12-L2 level laterally"],
    origin: "Sacrum, iliac crest, spinous and transverse processes",
    insertion: "Ribs, transverse processes, occiput (varies by column)",
    joints: ["Spine throughout"],
    clinical: "Three columns (iliocostalis, longissimus, spinalis). Hyperactive in chronic anterior pelvic tilt patterns.",
  },
  quadratus_lumborum: {
    region: "Lower Back",
    actions: ["Lateral flexion of trunk", "Elevates ilium (hip hike)", "Stabilizes 12th rib"],
    painReferral: ["Deep lateral low back", "Iliac crest", "Sacroiliac region", "Hip"],
    restrictions: ["Reduced contralateral side-bend", "Hip hike posture"],
    compensations: ["Limp/gait dysfunction", "Asymmetric pelvis"],
    related: ["psoas_iliacus", "erector_spinae", "gluteus_medius"],
    treatment: ["Side-lying access between 12th rib and iliac crest", "Slow, deep, sustained pressure"],
    cautions: ["Kidney just superior and anterior", "12th rib is floating"],
    origin: "Iliolumbar ligament, iliac crest",
    insertion: "12th rib, transverse processes L1-L4",
    joints: ["Lumbar, pelvis, rib 12"],
    clinical: "One of the most common drivers of low back pain. Often missed because pain refers far from origin.",
  },
  thoracolumbar_fascia: {
    region: "Lower Back",
    actions: ["Force transfer between trunk, pelvis, and lower limb", "Stabilization"],
    painReferral: ["Diffuse lumbar", "Sacroiliac region"],
    restrictions: ["Reduced trunk mobility", "Fascial stiffness"],
    compensations: ["Locked-up lumbar mobility"],
    related: ["erector_spinae", "lats_lower", "gluteus_maximus"],
    treatment: ["Slow, broad pressure with fists or forearm", "Skin rolling, myofascial release techniques"],
    cautions: ["Kidneys and lower ribs"],
    origin: "Lumbar spinous processes, sacrum, iliac crest",
    insertion: "Fascial sleeve over lumbar erectors",
    joints: ["Lumbar, sacroiliac"],
    clinical: "A key fascial node connecting upper and lower body force chains.",
  },

  // ───────── POSTERIOR ARM ─────────
  triceps_long: {
    region: "Hands/Forearms",
    actions: ["Elbow extension", "Shoulder extension and adduction"],
    painReferral: ["Posterior shoulder", "Down to olecranon"],
    restrictions: ["Reduced overhead reach", "Stiff elbow"],
    compensations: ["Lats dominance"],
    related: ["lats_upper", "teres_major", "triceps_lateral"],
    treatment: ["Stripping from infraglenoid tubercle to olecranon", "Pin and stretch in shoulder flexion"],
    cautions: ["Radial nerve in spiral groove"],
    origin: "Infraglenoid tubercle of scapula",
    insertion: "Olecranon of ulna",
    joints: ["Glenohumeral, elbow"],
    clinical: "Only triceps head that crosses the shoulder — restricts overhead motion when tight.",
  },
  triceps_lateral: {
    region: "Hands/Forearms",
    actions: ["Elbow extension"],
    painReferral: ["Lateral arm", "Lateral elbow"],
    restrictions: ["Reduced elbow flexion"],
    compensations: ["Overuse from pressing/typing"],
    related: ["triceps_long", "wrist_extensors"],
    treatment: ["Stripping along posterior humerus"],
    cautions: ["Radial nerve in spiral groove"],
    origin: "Posterior humerus above spiral groove",
    insertion: "Olecranon of ulna",
    joints: ["Elbow"],
    clinical: "Often involved in lateral arm pain mimicking lateral epicondylitis.",
  },
  wrist_extensors: {
    region: "Hands/Forearms",
    actions: ["Wrist extension", "Finger extension (deeper layers)"],
    painReferral: ["Lateral elbow (tennis elbow)", "Dorsum of forearm/hand"],
    restrictions: ["Reduced wrist flexion"],
    compensations: ["Grip overuse, mouse/keyboard"],
    related: ["brachioradialis", "wrist_flexors"],
    treatment: ["Stripping from lateral epicondyle distally", "Cross-fiber at common extensor origin"],
    cautions: ["Radial nerve branches"],
    origin: "Lateral epicondyle of humerus",
    insertion: "Carpals, metacarpals, phalanges",
    joints: ["Elbow, wrist, fingers"],
    clinical: "The classic tennis elbow location. Often pair with brachioradialis release.",
  },

  // ───────── GLUTES ─────────
  gluteus_maximus: {
    region: "Glutes",
    actions: ["Hip extension", "External rotation", "Abduction (upper fibers)"],
    painReferral: ["Posterior hip", "Sacroiliac", "Posterior thigh"],
    restrictions: ["Reduced hip flexion when contracted; weak in sitting cultures"],
    compensations: ["Hamstring dominance, lumbar extensor overuse"],
    related: ["gluteus_medius", "piriformis", "hamstrings_group", "thoracolumbar_fascia"],
    treatment: ["Broad pressure with forearm", "Trigger point release at attachments"],
    cautions: ["Sciatic nerve runs deep"],
    origin: "Posterior ilium, sacrum, coccyx",
    insertion: "IT band and gluteal tuberosity of femur",
    joints: ["Hip, sacroiliac"],
    clinical: "Often inhibited in chronic low back and knee pain — activation work matters as much as release.",
  },
  gluteus_medius: {
    region: "Glutes",
    actions: ["Hip abduction", "Anterior fibers: internal rotation", "Posterior fibers: external rotation"],
    painReferral: ["Lateral hip", "Sacroiliac", "Lateral thigh"],
    restrictions: ["Trendelenburg sign", "Pelvic drop in gait"],
    compensations: ["TFL overuse, QL hip hike"],
    related: ["tfl", "quadratus_lumborum", "piriformis"],
    treatment: ["Side-lying stripping", "Trigger point release at posterior fibers"],
    cautions: ["Superior gluteal nerve and artery"],
    origin: "Outer ilium between anterior and posterior gluteal lines",
    insertion: "Greater trochanter",
    joints: ["Hip, sacroiliac"],
    clinical: "Weakness here is a major driver of low back pain, knee pain, and gait dysfunction.",
  },
  piriformis: {
    region: "Glutes",
    actions: ["External rotation of hip (extended)", "Abduction (flexed)"],
    painReferral: ["Buttock", "Down posterior thigh (can mimic sciatica)"],
    restrictions: ["Reduced internal rotation"],
    compensations: ["Glute medius weakness, sciatica-like pain"],
    related: ["gluteus_medius", "gluteus_maximus"],
    treatment: ["Deep prone or side-lying pressure", "Stretch in figure-4 position"],
    cautions: ["Sciatic nerve runs deep (sometimes through) — slow, intelligent pressure"],
    origin: "Anterior sacrum",
    insertion: "Greater trochanter",
    joints: ["Hip, sacroiliac"],
    clinical: "Piriformis syndrome — often a key player in 'sciatica' without disc involvement.",
  },

  // ───────── HAMSTRINGS ─────────
  biceps_femoris: {
    region: "Hamstrings",
    actions: ["Knee flexion", "Hip extension", "External rotation of flexed knee"],
    painReferral: ["Posterior lateral thigh", "Lateral knee"],
    restrictions: ["Reduced knee extension with hip flexion (SLR)"],
    compensations: ["Glute weakness, anterior pelvic tilt"],
    related: ["semitendinosus", "semimembranosus", "gluteus_maximus"],
    treatment: ["Long stripping from ischial tuberosity to fibular head"],
    cautions: ["Common peroneal nerve near fibular head — avoid pressure"],
    origin: "Long head: ischial tuberosity; Short head: linea aspera",
    insertion: "Fibular head",
    joints: ["Hip (long head), knee"],
    clinical: "Most common hamstring strain location — frequent in athletes.",
  },
  semitendinosus: {
    region: "Hamstrings",
    actions: ["Knee flexion", "Hip extension", "Internal rotation of flexed knee"],
    painReferral: ["Posterior medial thigh", "Medial knee (pes anserine)"],
    restrictions: ["Reduced knee extension with hip flexion"],
    compensations: ["Glute weakness"],
    related: ["semimembranosus", "gracilis", "biceps_femoris"],
    treatment: ["Stripping along posterior medial thigh"],
    cautions: ["Sciatic nerve at proximal end"],
    origin: "Ischial tuberosity",
    insertion: "Pes anserinus (proximal medial tibia)",
    joints: ["Hip, knee"],
    clinical: "Pes anserine bursitis often involves this insertion.",
  },
  semimembranosus: {
    region: "Hamstrings",
    actions: ["Knee flexion", "Hip extension", "Internal rotation of flexed knee"],
    painReferral: ["Deep posterior medial knee"],
    restrictions: ["Reduced knee extension"],
    compensations: ["Posterior knee pain in cyclists"],
    related: ["semitendinosus", "biceps_femoris"],
    treatment: ["Deep stripping along medial posterior thigh"],
    cautions: ["Popliteal fossa contents distally"],
    origin: "Ischial tuberosity",
    insertion: "Posterior medial tibial condyle",
    joints: ["Hip, knee"],
    clinical: "Often involved in posterior knee pain and Baker's cyst presentations.",
  },

  // ───────── LATERAL THIGH ─────────
  IT_band: {
    region: "Quads",
    actions: ["Stabilizes lateral knee", "Force transmission from TFL and glute max"],
    painReferral: ["Lateral hip", "Lateral knee"],
    restrictions: ["Reduced hip adduction (Ober test)"],
    compensations: ["TFL/glute medius imbalance"],
    related: ["tfl", "gluteus_maximus", "vastus_lateralis"],
    treatment: ["Slow, broad pressure (not aggressive)", "Address TFL and glutes upstream first"],
    cautions: ["IT band is not a contractile structure — don't expect to 'release' it like a muscle"],
    origin: "Iliac crest (via TFL and glute max)",
    insertion: "Gerdy's tubercle (lateral tibia)",
    joints: ["Hip, knee"],
    clinical: "Treat the TFL and glutes, not the band itself, for lasting change.",
  },

  // ───────── CALVES ─────────
  gastrocnemius_medial: {
    region: "Calves",
    actions: ["Plantarflexion", "Knee flexion"],
    painReferral: ["Posterior medial calf", "Plantar heel"],
    restrictions: ["Reduced dorsiflexion with knee extended"],
    compensations: ["Heel-raised gait, plantar fasciitis"],
    related: ["gastrocnemius_lateral", "soleus", "hamstrings_group"],
    treatment: ["Long stripping from condyle to Achilles", "Cross-fiber at musculotendinous junction"],
    cautions: ["Popliteal artery and tibial nerve in popliteal fossa"],
    origin: "Medial femoral condyle",
    insertion: "Calcaneus via Achilles tendon",
    joints: ["Knee, ankle"],
    clinical: "Frequent driver of plantar fasciitis and Achilles tendinopathy.",
  },
  gastrocnemius_lateral: {
    region: "Calves",
    actions: ["Plantarflexion", "Knee flexion"],
    painReferral: ["Posterior lateral calf"],
    restrictions: ["Reduced ankle dorsiflexion"],
    compensations: ["Calf cramping patterns"],
    related: ["gastrocnemius_medial", "soleus"],
    treatment: ["Stripping along lateral calf"],
    cautions: ["Common peroneal nerve at fibular head"],
    origin: "Lateral femoral condyle",
    insertion: "Calcaneus via Achilles tendon",
    joints: ["Knee, ankle"],
    clinical: "Pairs with medial head; check both in chronic Achilles cases.",
  },
  soleus: {
    region: "Calves",
    actions: ["Plantarflexion (especially with knee bent)", "Postural ankle stabilization"],
    painReferral: ["Posterior calf", "Heel", "Sacroiliac (referred)"],
    restrictions: ["Reduced dorsiflexion with knee flexed"],
    compensations: ["Forward weight-shifted standing posture"],
    related: ["gastrocnemius_medial", "gastrocnemius_lateral"],
    treatment: ["Stripping deep to gastrocs in prone with knee bent", "Sustained pressure"],
    cautions: ["Posterior tibial artery and tibial nerve deep"],
    origin: "Soleal line of tibia, posterior fibula",
    insertion: "Calcaneus via Achilles tendon",
    joints: ["Ankle"],
    clinical: "Often the deeper source of chronic calf and heel pain — easy to under-treat.",
  },
};

/**
 * Get info for a single muscle; returns null when no entry exists.
 */
export function getMuscleInfo(id) {
  return MUSCLE_INFO[id] || null;
}
